/**
 * Plot Grid load errors (#819).
 *
 * POST /plottrack/map can fail because the story is gone, or because the
 * store/map failed (missing tables, 500, timeout). Those are not the same
 * sentence. Missing cell stays empty; this file is only the load-fail copy.
 */

export const PLOTTRACK_ERROR_STORY_MISSING = 'story_missing';
export const PLOTTRACK_ERROR_MAP = 'map_failed';

function errorText(error) {
  if (error == null) return '';
  if (typeof error === 'string') return error;
  return String(error.message || error.detail || '');
}

function errorStatus(error) {
  if (error == null || typeof error === 'string') return null;
  const status = error.status ?? error.response?.status;
  return typeof status === 'number' ? status : null;
}

export function isStoryMissingError(error) {
  const text = errorText(error).toLowerCase();
  const status = errorStatus(error);
  if (/story not found/.test(text)) return true;
  if (/plottrack grid not found|does not exist|undefined table|relation /.test(text)) {
    return false;
  }
  return status === 404;
}

export function classifyPlotTrackLoadError(error) {
  if (isStoryMissingError(error)) {
    return {
      kind: PLOTTRACK_ERROR_STORY_MISSING,
      title: 'This story is no longer available.',
      detail: null,
    };
  }

  const text = errorText(error).toLowerCase();
  const status = errorStatus(error);

  if (
    /does not exist|undefined table|relation /.test(text)
    || /plottrack_/.test(text)
  ) {
    return {
      kind: PLOTTRACK_ERROR_MAP,
      title: "Plot Grid couldn't load the outline.",
      detail: "The Plot Grid store isn't ready on this server yet. The book is still here.",
    };
  }

  if (
    /timeout|timed out|aborted/.test(text)
    || error?.name === 'AbortError'
    || status === 408
  ) {
    return {
      kind: PLOTTRACK_ERROR_MAP,
      title: "Plot Grid couldn't load the outline.",
      detail: 'The request timed out. The book is still here — try again.',
    };
  }

  if (
    (!status && /failed to fetch|network|load failed/.test(text))
    || status === 0
  ) {
    return {
      kind: PLOTTRACK_ERROR_MAP,
      title: "Plot Grid couldn't load the outline.",
      detail: "Plot Grid couldn't reach the server. The book is still here — try again.",
    };
  }

  if (status === 401) {
    return {
      kind: PLOTTRACK_ERROR_MAP,
      title: "Plot Grid couldn't load the outline.",
      detail: 'Sign-in expired. Sign in again, then try once more.',
    };
  }

  if (status === 403) {
    return {
      kind: PLOTTRACK_ERROR_MAP,
      title: "Plot Grid couldn't load the outline.",
      detail: "You don't have access to this book's Plot Grid map.",
    };
  }

  if (status >= 500) {
    return {
      kind: PLOTTRACK_ERROR_MAP,
      title: "Plot Grid couldn't load the outline.",
      detail: "The outline map failed on the server. The book is still here — try again.",
    };
  }

  return {
    kind: PLOTTRACK_ERROR_MAP,
    title: "Plot Grid couldn't load the outline.",
    detail: 'The outline map failed. The book is still here — try again.',
  };
}
